import ClassBreaksRenderer from '@arcgis/core/renderers/ClassBreaksRenderer'
import ClassBreakInfo from '@arcgis/core/renderers/support/ClassBreakInfo'
import CIMSymbol from '@arcgis/core/symbols/CIMSymbol'

import { IContinuousRoute } from '$/types'
import { IAcceptanceCategory } from 'app/services/api/acceptanceCategories'
import { formatHexToEsriRgb } from 'common/utils/colorUtils'

import { IClassBreakInfo } from 'features/Map/interfaces/Maps'
import {
  ILocalFilter,
  RiskUnitType
} from 'features/RiskManagement/Components/LocalFilter'

const LEGEND_TITLE_PLACEHOLDER = ' '

export enum RiskMapMode {
  QRA = 'QRA',
  Assessment = 'Assessment',
  Compare = 'Compare'
}

interface BuildRiskRendererParams {
  acceptanceCategories?: IAcceptanceCategory[]
  riskUnit: RiskUnitType
}

export const buildRiskRenderer = ({
  acceptanceCategories,
  riskUnit
}: BuildRiskRendererParams): ClassBreaksRenderer => {
  const field =
    riskUnit === RiskUnitType.RiskDensity ? 'RiskDensity' : 'RiskTotal'

  const classBreakInfos: IClassBreakInfo[] =
    acceptanceCategories?.map((category) => {
      const isRiskDensity = riskUnit === RiskUnitType.RiskDensity
      const maxValue = isRiskDensity
        ? category.maxDensityValue
        : category.maxValue
      const minValue = isRiskDensity
        ? category.minDensityValue
        : category.minValue

      return {
        label: `${category.name}: ${minValue.toLocaleString()} – ${
          maxValue >= Number.MAX_VALUE ? 'MAX' : maxValue.toLocaleString()
        } ${isRiskDensity ? 'RU/mi/yr' : 'RU/yr'}`,
        minValue,
        maxValue,
        color: category.colour,
        width: 4
      }
    }) ?? []

  return new ClassBreaksRenderer({
    legendOptions: {
      title: LEGEND_TITLE_PLACEHOLDER
    },
    field,
    classBreakInfos: classBreakInfos.map((item) => {
      return new ClassBreakInfo({
        label: item.label,
        minValue: item.minValue,
        maxValue: item.maxValue,
        symbol: new CIMSymbol({
          data: {
            type: 'CIMSymbolReference',
            symbol: {
              type: 'CIMLineSymbol',
              symbolLayers: [
                {
                  type: 'CIMSolidStroke',
                  enable: true,
                  capStyle: 'Butt',
                  joinStyle: 'Round',
                  width: item.width,
                  color: formatHexToEsriRgb(item.color)
                }
              ]
            }
          }
        })
      })
    })
  })
}

interface BuildRiskFilterQueryParams {
  continuousRoutes?: IContinuousRoute[]
  localFilter: ILocalFilter
  acceptanceCategories?: IAcceptanceCategory[]
  riskUnit: RiskUnitType
  continuousRouteId?: string
  mode: RiskMapMode
}

const buildSegmentClause = (
  segment: IContinuousRoute,
  mode: RiskMapMode
): string => {
  const baseClauses = [
    mode === RiskMapMode.Assessment
      ? `(FromMeasure <= ${segment.fromMeasure} AND ToMeasure >= ${segment.toMeasure} AND ToMeasure >= ${segment.fromMeasure})`
      : `(FromMeasure <= ${segment.fromMeasure} AND ToMeasure >= ${segment.toMeasure})`,
    `(FromMeasure >= ${segment.fromMeasure} AND ToMeasure <= ${segment.toMeasure})`,
    `(FromMeasure >= ${segment.fromMeasure} AND ToMeasure >= ${segment.toMeasure} AND FromMeasure < ${segment.toMeasure})`
  ]

  if (mode === RiskMapMode.QRA) {
    baseClauses.push(
      `(FromMeasure <= ${segment.fromMeasure} AND ToMeasure <= ${segment.toMeasure} AND ToMeasure >= ${segment.fromMeasure})`
    )
  }

  return `((ContinuousRouteId = '${segment.continuousRouteId}') AND (${baseClauses.join(
    ' OR '
  )}))`
}

const buildRiskLevelClauses = (
  localFilter: ILocalFilter,
  acceptanceCategories: IAcceptanceCategory[] | undefined,
  riskUnit: RiskUnitType
): string[] => {
  if (!acceptanceCategories?.length || !localFilter.riskLevels?.length) {
    return []
  }

  const isRiskDensity = riskUnit === RiskUnitType.RiskDensity

  return localFilter.riskLevels.reduce<string[]>((clauses, riskLevel) => {
    const matchedCategory = acceptanceCategories.find(
      (category) => category.code.toString() === riskLevel.code.toString()
    )

    if (!matchedCategory) {
      return clauses
    }

    const minValue = isRiskDensity
      ? matchedCategory.minDensityValue
      : matchedCategory.minValue
    const maxValue = isRiskDensity
      ? matchedCategory.maxDensityValue
      : matchedCategory.maxValue

    clauses.push(
      isRiskDensity
        ? `(RiskDensity >= ${minValue} AND RiskDensity <= ${maxValue})`
        : `(RiskTotal >= ${minValue} AND RiskTotal <= ${maxValue})`
    )

    return clauses
  }, [])
}

const filterSegmentsByRouteId = (
  segments: IContinuousRoute[],
  continuousRouteId?: string
): IContinuousRoute[] => {
  if (!continuousRouteId) {
    return segments
  }

  const parsedRouteId = Number(continuousRouteId)

  return segments.filter((segment) => {
    return (
      segment.id === parsedRouteId ||
      segment.continuousRouteId === parsedRouteId
    )
  })
}

interface BuildSystemScopeClauseParams {
  systemScopeRoutes: IContinuousRoute[]
  localSegments: IContinuousRoute[]
  continuousRouteId?: string
  mode: RiskMapMode
}

const buildSystemScopeClause = ({
  systemScopeRoutes,
  localSegments,
  continuousRouteId,
  mode
}: BuildSystemScopeClauseParams): string | undefined => {
  if (systemScopeRoutes.length === 0) {
    return undefined
  }

  if (localSegments.length > 0) {
    const filteredSegments = filterSegmentsByRouteId(
      localSegments,
      continuousRouteId
    )

    if (filteredSegments.length === 0) {
      return undefined
    }

    const segmentClauses = filteredSegments.map((segment) =>
      buildSegmentClause(segment, mode)
    )

    if (segmentClauses.length === 0) {
      return undefined
    }

    return `(${segmentClauses.join(' OR ')})`
  }

  if (continuousRouteId === undefined) {
    const routeIds = systemScopeRoutes.reduce<string[]>((ids, route) => {
      const found = localSegments.some(
        (segment) => route.id === segment.continuousRouteId
      )

      if (!found) {
        ids.push(`'${route.id}'`)
      }

      return ids
    }, [])

    if (routeIds.length === 0) {
      return undefined
    }

    return `ContinuousRouteId IN (${routeIds.join(', ')})`
  }

  return `ContinuousRouteId = '${continuousRouteId}'`
}

const buildClassLocationClause = (
  classLocations: ILocalFilter['classLocations']
): string | undefined => {
  if (!classLocations?.length) {
    return undefined
  }

  const classLocationClauses = classLocations.map(
    (classLocation) => `ClassLocationCode = '${classLocation.code}'`
  )

  return `(${classLocationClauses.join(' OR ')})`
}

const buildHmcaClause = (hmca: ILocalFilter['hmca']): string | undefined => {
  if (!hmca?.length) {
    return undefined
  }

  const hmcaClauses = hmca.map((area) => `ConsequenceAreaCode = '${area.code}'`)

  return `(${hmcaClauses.join(' OR ')})`
}

const buildRiskLevelClause = (
  localFilter: ILocalFilter,
  acceptanceCategories: IAcceptanceCategory[] | undefined,
  riskUnit: RiskUnitType
): string | undefined => {
  const riskLevelClauses = buildRiskLevelClauses(
    localFilter,
    acceptanceCategories,
    riskUnit
  )

  if (riskLevelClauses.length === 0) {
    return undefined
  }

  if (riskLevelClauses.length === 1) {
    return riskLevelClauses[0]
  }

  return `(${riskLevelClauses.join(' OR ')})`
}

export const buildRiskFilterQuery = ({
  continuousRoutes,
  localFilter,
  acceptanceCategories,
  riskUnit,
  continuousRouteId,
  mode
}: BuildRiskFilterQueryParams): string => {
  if (mode === RiskMapMode.Compare) {
    // TODO: implement RiskMapMode.Compare
    return ''
  }

  const systemScopeRoutes = continuousRoutes ?? []
  const localSegments = [...(localFilter.segments ?? [])]

  const clauses: string[] = []

  const systemScopeClause = buildSystemScopeClause({
    systemScopeRoutes,
    localSegments,
    continuousRouteId,
    mode
  })

  if (systemScopeClause) {
    clauses.push(systemScopeClause)
  }

  const classLocationClause = buildClassLocationClause(
    localFilter.classLocations
  )

  if (classLocationClause) {
    clauses.push(classLocationClause)
  }

  const hmcaClause = buildHmcaClause(localFilter.hmca)

  if (hmcaClause) {
    clauses.push(hmcaClause)
  }

  const riskLevelClause = buildRiskLevelClause(
    localFilter,
    acceptanceCategories,
    riskUnit
  )

  if (riskLevelClause) {
    clauses.push(riskLevelClause)
  }

  return clauses.join(' AND ')
}
